import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  DEFAULT_WORKING_DAYS, coversProject, dueTone, excerpt, isDoneGroup, orderKey,
  STATE_GROUPS, type ProjectScope,
} from '@kolibri/shared';
import { Header, Trail, type Crumb } from '../../../kernel/design-system/chrome';
import { QuickAdd } from '../../work/QuickAdd';
import { CycleProgress, TaskViews, useVisibleTasks, ViewControls } from '../../work/views';
import { DEFAULT_VIEW, type ViewConfig } from '../../work/task-parts';
import { useSelection } from '../../../kernel/design-system/selection';
import { SelectionBar } from '../../work/selection-bar';
import { ProjectTime } from '../../time/time';
import { ForeignImportSheet, ImportSheet, type Inspection } from '../../../adapters/transfer/import';
import { ProjectInsights } from '../insights';
import { ProjectScopeField, useScopeChoice } from '../scope-field';
/**
 * The KPI panels a project page and a milestone page host.
 *
 * Fetched rather than imported, because a static import here is what put the
 * whole KPI screen — 1 676 lines a workspace with the switch off never renders
 * — back into the first load, defeating the split in `App.tsx`. Both are behind
 * `useFeature('kpi')` at the call sites below, so nothing is fetched until a
 * workspace that has KPIs on opens a page that shows them.
 *
 * That a planning screen reaches into the KPI capability at all is finding 4 in
 * `docs/modules.md`. This makes the reach visible and cheap; it does not
 * pretend to remove it — the module contract's slots are what does that.
 */
const ProjectKpis = lazy(() => import('../../kpis/routes/kpis').then((m) => ({ default: m.ProjectKpis })));
const MilestoneKpis = lazy(() => import('../../kpis/routes/kpis').then((m) => ({ default: m.MilestoneKpis })));
/**
 * The budget panel a project page hosts. Fetched for the same reason the KPI
 * panels above are: a static import pulls every money component into the first
 * load of a workspace that has budgets switched off.
 */
const ProjectBudget = lazy(() => import('../../budgets/budget').then((m) => ({ default: m.ProjectBudget })));
import { Markdown, MarkdownEditor } from '../../pages/Markdown';
import { Avatar, Empty, Icon, MenuButton, Progress, Sheet, useConfirm, useToast } from '../../../kernel/design-system/ui';
import { GuideHint } from '../../guide/hint';
import { api } from '../../../kernel/sync/api';
import { shortDate, today } from '../../../kernel/design-system/format';
import { useTabStrip } from '../../../kernel/design-system/tab-strip';
import { byOrder, create, createPage, remove, update } from '../../../kernel/sync/mutations';
import { useOpenTask } from '../../../kernel/design-system/navigation';
import { byId, list, useQuery, useRow } from '../../../kernel/sync/store';
import { pull } from '../../../kernel/sync/sync';
import { useCanWrite, useFeature, useMe, useMembers, useSession } from '../../../kernel/identity/session';
import { groupKey, roleKey, useT, type TranslationKey } from '../../../kernel/i18n/i18n';
import { ProjectFields } from '../../work/fields';
import { CopyProjectSheet } from '../copy-project';
import { configOf, useProjectDefaultView } from '../../work/saved-views';
import { Button } from '../../../kernel/design-system/ui/button';
import { buttonVariants } from '../../../kernel/design-system/ui/button';
import { cn } from '../../../kernel/design-system/cn';
import { Input, Select } from '../../../kernel/design-system/ui/field';
import { EmojiPicker } from '../../../kernel/design-system/ui/emoji-picker';
import { SectionHeading } from '../../../kernel/design-system/ui/section';
import { Chip, chipDot, chipVariants } from '../../../kernel/design-system/ui/chip';
import { Triage, useNewIntakeCount } from '../../intake/intake';
import { Attachments } from '../../../kernel/files/Attachments';

const VIEW_KEY = (projectId: string) => `kolibri.view.${projectId}`;

const TAB_KEY: Record<string, TranslationKey> = {
  tasks: 'project.tabTasks', cycles: 'project.tabCycles', modules: 'project.tabModules',
  pages: 'project.tabPages', intake: 'intake.tab', insights: 'insights.tab', budget: 'budget.tab',
  kpis: 'kpi.title', settings: 'project.tabSettings',
};

/** Monday first, because a working week starts on one. `day` is getUTCDay. */
const WEEKDAYS: { day: number; key: TranslationKey }[] = [
  { day: 1, key: 'view.weekdayMon' }, { day: 2, key: 'view.weekdayTue' }, { day: 3, key: 'view.weekdayWed' },
  { day: 4, key: 'view.weekdayThu' }, { day: 5, key: 'view.weekdayFri' }, { day: 6, key: 'view.weekdaySat' },
  { day: 0, key: 'view.weekdaySun' },
];

/**
 * The view this project is being looked at through.
 *
 * Three answers, in order: what this device last chose, what the project was
 * pinned to open on, and the plain list. The device wins because a view is a
 * way of *looking* — somebody who switched to the board and came back tomorrow
 * meant it, and a project default that overruled them every morning would be a
 * setting that fights its users.
 */
function useStoredView(projectId: string): [ViewConfig, (next: ViewConfig) => void] {
  const me = useMe();
  const pinned = useProjectDefaultView(projectId, me);
  const [chosenHere] = useState<boolean>(() => {
    try {
      return !!localStorage.getItem(VIEW_KEY(projectId));
    } catch {
      return false;
    }
  });
  const [view, setView] = useState<ViewConfig>(() => {
    try {
      const raw = localStorage.getItem(VIEW_KEY(projectId));
      return raw ? { ...DEFAULT_VIEW, ...JSON.parse(raw) } : DEFAULT_VIEW;
    } catch {
      return DEFAULT_VIEW;
    }
  });

  // The pin arrives with the sync rather than with the first render, so it is
  // applied when it turns up — once, and never over a choice made on this
  // device, including one made in the second between the two.
  const applied = useRef('');
  useEffect(() => {
    if (chosenHere || !pinned || applied.current === projectId) return;
    applied.current = projectId;
    setView(configOf(pinned));
  }, [chosenHere, pinned, projectId]);

  return [
    view,
    (next: ViewConfig) => {
      setView(next);
      applied.current = projectId;
      localStorage.setItem(VIEW_KEY(projectId), JSON.stringify(next));
    },
  ];
}

/* --------------------------------------------------------------- overview */

export function ProjectList() {
  const t = useT();
  const { workspaceId } = useSession();
  const navigate = useNavigate();
  const projects = useQuery(() => list('project', (p) => p.workspace_id === workspaceId), [workspaceId]);
  const canWrite = useCanWrite();

  return (
    <>
      <Header title={t('project.listTitle')}>
        {canWrite && (
          <Button variant="primary" size="sm" onClick={() => navigate('/projects/new')}><Icon name="plus" size={14} /> {t('action.create')}</Button>
        )}
      </Header>
      <div className="mx-auto max-w-[1180px] px-3 pb-20 pt-4 sm:px-6 sm:pb-16 sm:pt-5">
        <div className="grid gap-3 sm:grid-cols-2">
          {projects.map((project) => <ProjectCard key={project.id} projectId={project.id} />)}
        </div>
        {!projects.length && (
          <Empty
            emoji="📁" title={t('project.emptyTitle')} hint={t('project.emptyHint')} guide="overview"
            action={canWrite
              ? <Button variant="primary" onClick={() => navigate('/projects/new')}>{t('project.createCta')}</Button>
              : undefined}
          />
        )}
      </div>
    </>
  );
}

function ProjectCard({ projectId }: { projectId: string }) {
  const t = useT();
  const navigate = useNavigate();
  const project = useRow('project', projectId);
  const tasks = useQuery(() => list('task', (t) => t.project_id === projectId && !t.archived), [projectId]);
  const done = tasks.filter((task) => isDoneGroup(byId('state', task.state_id)?.group_key)).length;
  if (!project) return null;

  return (
    <button className="rounded-[var(--radius)] border border-line bg-raised p-3.5 text-left cursor-pointer" onClick={() => navigate(`/projects/${projectId}`)}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-lg">{project.icon ?? '📁'}</span>
        <strong className="flex-1 min-w-0 truncate">{project.name}</strong>
        <Chip className="font-mono">{project.key}</Chip>
      </div>
      {/* Stripped, not rendered: this is one truncated line inside a card, and
          a heading or a list dropped into it would break the row rather than
          say anything. `excerpt` is the same function the pages index uses for
          the same reason — markdown out, the sentence left. */}
      {project.description && (
        <p className="text-muted truncate text-[12.5px]">{excerpt(project.description, 140)}</p>
      )}
      <Progress value={done} total={tasks.length} />
      <div className="flex items-center gap-2 text-muted text-[12.5px] mt-1.5">
        <span>{t('project.doneCount', { done, total: tasks.length })}</span>
        <span className="flex-1 min-w-0" />
        {project.target_date && <span>{t('project.target', { date: shortDate(project.target_date) })}</span>}
      </div>
    </button>
  );
}

export function ProjectNew() {
  const t = useT();
  const { workspaceId } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  // `?parent=` so "New sub-project" in a project's menu arrives here with the
  // answer already filled in, rather than asking again for something the
  // person has just said.
  const [search] = useSearchParams();
  const [form, setForm] = useState({
    name: '', key: '', description: '', icon: '🚀', visibility: 'public',
    parent_id: search.get('parent') ?? '',
  });
  const [busy, setBusy] = useState(false);

  const parents = useQuery(
    () => list('project', (row) => row.workspace_id === workspaceId && !row.archived)
      .sort((a, b) => a.name.localeCompare(b.name)),
    [workspaceId],
  );

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    try {
      // Projects are created server-side so they get their workflow states,
      // labels and a unique key in one transaction.
      const project = await api.post<any>(`/api/workspaces/${workspaceId}/projects`, form);
      await pull();
      navigate(`/projects/${project.id}`);
    } catch (err) {
      toast(err instanceof Error ? err.message : t('project.createFailed'));
      setBusy(false);
    }
  }

  return (
    <>
      <Header title={t('project.new')} />
      <div className="mx-auto max-w-[1180px] px-3 pb-20 pt-4 sm:px-6 sm:pb-16 sm:pt-5" style={{ maxWidth: 560 }}>
        <form onSubmit={submit}>
          <div className="field">
            <label htmlFor="p-name">{t('project.name')}</label>
            <Input
              id="p-name" required autoFocus value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              placeholder={t('project.namePlaceholder')}
            />
          </div>
          <div className="flex items-start gap-2.5">
            <div className="field" style={{ width: 60 }}>
              <label htmlFor="p-icon">{t('project.icon')}</label>
              <EmojiPicker
                id="p-icon" value={form.icon} fallback="🚀"
                onChange={(next) => setForm({ ...form, icon: next ?? '' })}
              />
            </div>
            <div className="field flex-1 min-w-0">
              <label htmlFor="p-key">{t('project.key')}</label>
              <Input className="mono"
                id="p-key" value={form.key} maxLength={6} placeholder={t('project.keyAuto')}
                onChange={(event) => setForm({ ...form, key: event.target.value.toUpperCase() })}
              />
              <span className="text-[12px] text-muted">{t('project.keyHint')}</span>
            </div>
          </div>
          <div className="field">
            {/* No `htmlFor`: the editor is a toolbar and a textarea rather
                than one control, so the id would name whichever half happened
                to carry it. Same as the rule body in `automation.tsx`, which
                is the other markdown field inside a form. */}
            <label>{t('project.description')}</label>
            <MarkdownEditor
              value={form.description} minHeight={90}
              onChange={(value) => setForm({ ...form, description: value })}
            />
          </div>
          <div className="field">
            <label htmlFor="p-parent">{t('project.parent')}</label>
            <Select id="p-parent" value={form.parent_id}
              onChange={(event) => setForm({ ...form, parent_id: event.target.value })}
            >
              <option value="">{t('project.parentNone')}</option>
              {parents.map((other) => (
                <option key={other.id} value={other.id}>{other.icon} {other.name}</option>
              ))}
            </Select>
            <span className="text-[12px] text-muted">{t('project.parentHint')}</span>
          </div>
          <div className="field">
            <label htmlFor="p-vis">{t('project.visibility')}</label>
            <Select id="p-vis" value={form.visibility} onChange={(event) => setForm({ ...form, visibility: event.target.value })}>
              <option value="public">{t('project.visibilityPublic')}</option>
              <option value="private">{t('project.visibilityPrivate')}</option>
            </Select>
          </div>
          {/* `type="submit"` is not decoration: `Button` defaults to
              `type="button"`, which is right everywhere except inside a form,
              and a submit button that quietly does nothing is exactly the bug
              that got here. `test/forms.test.ts` now refuses the next one. */}
          <Button variant="primary" size="lg" block type="submit" disabled={busy || !form.name.trim()}>
            {busy ? t('project.creating') : t('project.createSubmit')}
          </Button>
        </form>
      </div>
    </>
  );
}

/**
 * What sits under a container, as cards.
 *
 * Each child carries the only two numbers worth showing at this level: how much
 * of it is done, and whether anything in it is late. A container is a reading
 * screen — somebody opens it to see where things stand across four projects,
 * not to move a card.
 */
function ContainerChildren({ projectId }: { projectId: string }) {
  const t = useT();
  const navigate = useNavigate();
  const children = useQuery(
    () => list('project', (row) => row.parent_id === projectId && !row.archived)
      .sort((a, b) => a.name.localeCompare(b.name)),
    [projectId],
  );
  const tasks = useQuery(() => list('task', (task) => !task.archived), []);

  if (!children.length) {
    return (
      <div className="mx-auto max-w-[1180px] px-3 pb-20 pt-4 sm:px-6 sm:pb-16 sm:pt-5">
        <Empty
          emoji="🗂️"
          title={t('project.containerEmpty')}
          hint={t('project.containerEmptyHint')}
        />
      </div>
    );
  }

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="mx-auto max-w-[1180px] px-3 pb-20 pt-4 sm:px-6 sm:pb-16 sm:pt-5">
      <p className="text-muted text-[12.5px] mb-2.5">{t('project.containerCount', { count: children.length })}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {children.map((child) => {
          const mine = tasks.filter((task) => task.project_id === child.id);
          const done = mine.filter((task) => byId('state', task.state_id)?.group_key === 'completed').length;
          const late = mine.filter((task) =>
            dueTone(task.due_date, byId('state', task.state_id)?.group_key, today) === 'overdue').length;
          return (
            <button
              key={child.id}
              type="button"
              className="rounded-[var(--radius)] border border-line bg-raised p-3.5 text-start"
              onClick={() => navigate(`/projects/${child.id}`)}
            >
              <span className="flex items-center gap-2 mb-2">
                <span className="text-lg">{child.icon ?? '📁'}</span>
                <span className="flex-1 min-w-0 truncate font-semibold">{child.name}</span>
                {child.is_container ? <Icon name="folder" size={14} /> : <Chip className="font-mono">{child.key}</Chip>}
              </span>
              {child.is_container ? (
                <span className="text-muted text-[12.5px]">{t('project.isContainer')}</span>
              ) : (
                <>
                  <Progress value={done} total={mine.length} />
                  <span className="flex items-center gap-2 mt-1.5 text-[12.5px] text-muted">
                    <span>{t('project.doneOf', { done, total: mine.length })}</span>
                    {late > 0 && <span className="text-danger">{t('portfolio.lateCount', { count: late })}</span>}
                  </span>
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- project */

const TABS = ['tasks', 'cycles', 'modules', 'pages', 'intake', 'insights', 'budget', 'kpis', 'settings'] as const;

/**
 * What a container shows instead.
 *
 * Cycles, modules, reports and insights are all about work, and a container has
 * none — a screen full of tabs that each say "nothing here" is worse than four
 * tabs that mean something. Pages stay, because a place to group projects is
 * exactly where the note explaining the grouping belongs.
 */
const CONTAINER_TABS = ['tasks', 'pages', 'settings'] as const;
type Tab = (typeof TABS)[number];

export function ProjectPage() {
  const t = useT();
  const openTask = useOpenTask();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const project = useRow('project', id);
  const [view, setView] = useStoredView(id);
  const selection = useSelection();
  const canWrite = useCanWrite();
  const isContainer = !!project?.is_container;
  const budgets = useFeature('budget');
  const kpis = useFeature('kpi');
  // `?tab=` so a link can point at one — a notification about a report has to
  // land on the reports, not on the task list beside them.
  const [search, setSearch] = useSearchParams();
  const asked = search.get('tab');
  const [tab, setTab] = useState<Tab>(TABS.includes(asked as Tab) ? asked as Tab : 'tasks');
  const strip = useTabStrip(tab);
  const [adding, setAdding] = useState(false);

  const tasks = useQuery(() => list('task', (t) => t.project_id === id && !t.parent_id), [id]);
  const visible = useVisibleTasks(tasks, view);
  const waitingReports = useNewIntakeCount(id);

  if (!project) {
    return (
      <>
        <Header title={t('nav.projects')} />
        {/* Archived, deleted, or in a workspace this account left: all three
            open from a bookmark, and all three used to be a dead end. */}
        <div className="px-3 pt-4 sm:px-6 sm:pt-5">
          <Trail parts={[{ to: '/projects', label: t('nav.projects'), icon: <Icon name="board" size={13} /> }]} />
        </div>
        <Empty
          emoji="🕳️" title={t('project.notFound')} hint={t('project.notFoundHint')}
          action={<Button onClick={() => navigate('/projects')}>{t('nav.backToList')}</Button>}
        />
      </>
    );
  }

  // Projects nest, so the trail walks up the same way the wiki's does — and
  // stops on a parent it cannot see rather than looping on one that arrived
  // reparented over sync.
  const ancestors: typeof project[] = [];
  const seen = new Set<string>([project.id]);
  for (let at = project.parent_id ?? null; at && !seen.has(at);) {
    const parent = byId('project', at);
    if (!parent) break;
    seen.add(at);
    ancestors.unshift(parent);
    at = parent.parent_id ?? null;
  }
  const crumbs: Crumb[] = [
    { to: '/projects', label: t('nav.projects'), icon: <Icon name="board" size={13} /> },
    ...ancestors.map((one) => ({ to: `/projects/${one.id}`, label: one.name, icon: one.icon })),
  ];

  return (
    <>
      <Header title={<span className="flex items-center gap-2" style={{ gap: 7 }}><span>{project.icon}</span> {project.name}</span>}>
        {tab === 'tasks' && !isContainer && <ViewControls view={view} onChange={setView} projectId={id} saveable />}
        {canWrite && (isContainer ? (
          // The button a container needs. "New task" on a screen with no board
          // is a button that files work where nobody will look for it.
          <Button variant="primary" size="sm" onClick={() => navigate(`/projects/new?parent=${id}`)}>
            <Icon name="plus" size={14} /> <span className="hide-sm">{t('project.newSub')}</span>
          </Button>
        ) : (
          <Button variant="primary" size="sm" onClick={() => setAdding(true)}>
            <Icon name="plus" size={14} /> <span className="hide-sm">{t('nav.newTask')}</span>
          </Button>
        ))}
      </Header>

      {/* Above the tabs, not below: the tabs are places inside this project, and
          the trail is the way out of it. Mixing the two would read as a tenth
          tab. */}
      <div className="px-3 pt-3 sm:px-6">
        <Trail parts={crumbs} />
      </div>

      <div ref={strip} className="tabs" style={{ padding: '0 12px' }}>
        {/* Reports is always here, even for a project that will never use it.
            Hiding it until an intake link exists would make the one screen that
            explains how to get one the screen nobody can find. */}
        {(isContainer ? CONTAINER_TABS : TABS)
          // Budgets only when the workspace runs them. A tab that always says
          // "nothing charges this project" is a tab that teaches people to stop
          // reading the strip.
          .filter((name) => name !== 'budget' || budgets)
          .filter((name) => name !== 'kpis' || kpis)
          .map((name) => (
          <button
            key={name}
            className={tab === name ? 'active' : ''}
            onClick={() => {
              setTab(name);
              // The URL follows the tab, so a reload and a back button both
              // land where the person was rather than on the task list.
              setSearch(name === 'tasks' ? {} : { tab: name }, { replace: true });
            }}
          >
            {/* A container's first tab shows projects, not tasks — the label
                has to say what is behind it, not what it is behind on every
                other project. */}
            {t(isContainer && name === 'tasks' ? 'nav.projects' : TAB_KEY[name])}
            {name === 'intake' && waitingReports > 0 && <span className="tab-count">{waitingReports}</span>}
          </button>
        ))}
      </div>

      {/*
        The description, where the screen has room for it.

        Not above a board: the board is sized as the whole rest of the window
        (`100dvh` less the header and the tabs), so anything put above it pushes
        the bottom of every column off the screen. A board is the screen it is
        on. Every other layout — and a container, which has no board at all —
        scrolls, and a project that says what it is for at the top of it is
        worth more there than the two lines it costs.

        A project with no description gets nothing, which is most of them.
      */}
      {tab === 'tasks' && (isContainer || view.layout !== 'board') && project.description && (
        <div className="mx-auto max-w-[1180px] px-3 pt-4 sm:px-6 sm:pt-5 text-[13px]">
          <Markdown source={project.description} />
        </div>
      )}
      {tab === 'tasks' && isContainer && <ContainerChildren projectId={id} />}
      {tab === 'tasks' && !isContainer && (
        view.layout === 'board'
          ? <div style={{ height: 'calc(100dvh - var(--header-height) - 110px)' }}>
            <TaskViews tasks={visible} view={view} projectId={id} onOpen={openTask} />
          </div>
          : <div className="mx-auto max-w-[1180px] px-3 pb-20 pt-4 sm:px-6 sm:pb-16 sm:pt-5" style={{ paddingInline: 0 }}>
            <TaskViews
              tasks={visible} view={view} projectId={id} onOpen={openTask}
              onChange={setView} selection={selection}
            />
          </div>
      )}
      {tab === 'tasks' && !isContainer && <SelectionBar selection={selection} tasks={visible} />}
      {tab === 'cycles' && <Cycles projectId={id} />}
      {tab === 'modules' && <Modules projectId={id} />}
      {tab === 'pages' && <ProjectPages projectId={id} />}
      {tab === 'intake' && <Triage projectId={id} />}
      {tab === 'insights' && <ProjectInsights projectId={id} />}
      {tab === 'budget' && budgets && <Suspense fallback={null}><ProjectBudget projectId={id} /></Suspense>}
      {tab === 'kpis' && kpis && <Suspense fallback={null}><ProjectKpis projectId={id} /></Suspense>}
      {tab === 'settings' && <ProjectSettings projectId={id} />}

      {adding && <QuickAdd projectId={id} onClose={() => setAdding(false)} />}
    </>
  );
}

/* ----------------------------------------------------------------- cycles */

function Cycles({ projectId }: { projectId: string }) {
  const t = useT();
  const navigate = useNavigate();
  // This project's own, plus the ones it shares: a cycle that names it, and a
  // cycle every project runs. `coversProject` is the one place that question is
  // answered, so this tab and the server's queries cannot drift apart.
  const cycles = useQuery(
    () => list('cycle', (c) => coversProject(c, projectId))
      .sort((a, b) => (b.start_date ?? '').localeCompare(a.start_date ?? '')),
    [projectId],
  );
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const day = today();

  return (
    <div className="mx-auto max-w-[1180px] px-3 pb-20 pt-4 sm:px-6 sm:pb-16 sm:pt-5">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-[15px]">{t('cycle.title')}</h2>
        <span className="flex-1 min-w-0" />
        <Button size="sm" onClick={() => setEditing('new')}><Icon name="plus" size={14} /> {t('cycle.new')}</Button>
      </div>

      {!cycles.length && <Empty emoji="🔁" title={t('cycle.emptyTitle')} hint={t('cycle.emptyHint')} guide="planning" />}

      <div className="grid gap-3 sm:grid-cols-2">
        {cycles.map((cycle) => {
          const active = cycle.start_date && cycle.end_date && cycle.start_date <= day && cycle.end_date >= day;
          return (
            <div className="rounded-[var(--radius)] border border-line bg-raised p-3.5" key={cycle.id}>
              <div className="flex items-center gap-2 mb-2">
                <strong className="flex-1 min-w-0 truncate">{cycle.name}</strong>
                {/* Said on the card rather than only in the editor: deleting a
                    shared cycle takes it out of every project that is in it,
                    and the menu below offers exactly that. The chip counts the
                    projects when it covers some, because "Shared" alone leaves
                    the reader to guess whether that means two or twelve. */}
                {!cycle.project_id && (
                  <span
                    className={chipVariants()}
                    title={cycle.projects?.length ? t('cycle.sharedSomeHint') : t('cycle.sharedHint')}
                  >
                    {cycle.projects?.length
                      ? t('cycle.sharedCount', { count: cycle.projects.length })
                      : t('cycle.shared')}
                  </span>
                )}
                {active && <span className={chipVariants()} style={{ borderColor: 'var(--accent)', color: 'var(--accent)' }}>{t('cycle.active')}</span>}
                <MenuButton
                  variant="ghost" size="iconSm"
                  label={t('common.moreActions')}
                  items={[
                    { id: 'edit', label: t('action.edit'), onSelect: () => setEditing(cycle.id) },
                    { id: 'open', label: t('cycle.showTasks'), onSelect: () => navigate(`/cycles/${cycle.id}`) },
                    { id: 'delete', label: t('action.delete'), danger: true, onSelect: () => remove('cycle', cycle.id) },
                  ]}
                >
                  <Icon name="dots" size={14} />
                </MenuButton>
              </div>
              <CycleProgress cycleId={cycle.id} />
              <Button size="sm" block className="mt-2.5" onClick={() => navigate(`/cycles/${cycle.id}`)}>{t('cycle.open')}</Button>
            </div>
          );
        })}
      </div>

      {editing && (
        <CycleEditor
          projectId={projectId}
          cycleId={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/**
 * Making a cycle, and changing one — including what it covers.
 *
 * The scope used to be create-only, on the argument that re-scoping a running
 * cycle can strand work and that doing so behind a dropdown which looks like a
 * setting is how data goes missing without anybody attributing it to the click.
 * The argument was right about the danger and wrong about the remedy: it left
 * the only way to widen a fortnight to a second project an MCP call, so the
 * answer to "add Mobile to this sprint" in the interface was to delete the
 * cycle and build it again — which strands *everything* in it rather than the
 * part that no longer fits.
 *
 * So the control is here, and the consequence is on screen *before* the save
 * rather than reported after it: the tasks that would be left in a cycle their
 * project is no longer covered by are named, by identifier, as the scope is
 * chosen. Named rather than counted, for the reason `update_cycle` names them —
 * "3 tasks" is a number somebody then has to go and find.
 *
 * Nothing is moved either way. A task keeps its `cycle_id`; dropping a project
 * out of a cycle is a decision about the cycle, and taking somebody's task out
 * of a sprint is a different decision that is not this form's to make. The
 * server applies the same rule, so an import and an assistant land the same
 * shape — see `settleProjectScope` and the planning rules.
 */
function CycleEditor({ projectId, cycleId, onClose }: { projectId: string; cycleId?: string; onClose: () => void }) {
  const t = useT();
  const cycle = useRow('cycle', cycleId);
  const { confirm, dialog } = useConfirm();
  const [form, setForm] = useState({
    name: cycle?.name ?? '',
    description: cycle?.description ?? '',
    start_date: cycle?.start_date ?? today(),
    end_date: cycle?.end_date ?? new Date(Date.now() + 14 * 86_400_000).toISOString().slice(0, 10),
  });
  const choice = useScopeChoice(cycleId ? cycle : undefined, projectId);
  const projects = useScopeTargets();
  const stranded = useStranded('cycle_id', cycleId, choice.settled);

  const save = async () => {
    if (!form.name.trim() || choice.incomplete) return;
    if (cycleId) {
      /* Asked once, and only when there is actually something to lose: the
         names are already on screen, and this is the deliberate second step a
         delete has. The label is given because `confirm` says "Delete" by
         default and nothing here is deleted — a danger button reading "Delete"
         over a sentence promising nothing is removed is a contradiction the
         reader has to resolve under time pressure. */
      if (stranded.length
        && !(await confirm(t('cycle.scopeStrandConfirm', { count: stranded.length }), t('action.save')))) return;
      update('cycle', cycleId, { ...form, ...choice.settled });
    } else {
      // `projectScope` is what the server would apply anyway; applying it here
      // too means the row this device writes into its own store is already the
      // canonical shape, rather than one that changes under it on the next pull.
      create('cycle', { ...form, ...choice.settled });
    }
    onClose();
  };

  return (
    <>
      <Sheet
        title={cycleId ? t('cycle.edit') : t('cycle.new')}
        onClose={onClose}
        footer={(
          <Button variant="primary" onClick={() => void save()} disabled={!form.name.trim() || choice.incomplete}>
            {t('action.save')}
          </Button>
        )}
      >
        <div className="field">
          <label htmlFor="c-name">{t('cycle.name')}</label>
          <Input id="c-name" autoFocus value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
        </div>
        <div className="field-row">
          <div className="field flex-1 min-w-0">
            <label htmlFor="c-start">{t('cycle.starts')}</label>
            <Input id="c-start" type="date" value={form.start_date ?? ''} onChange={(event) => setForm({ ...form, start_date: event.target.value })} />
          </div>
          <div className="field flex-1 min-w-0">
            <label htmlFor="c-end">{t('cycle.ends')}</label>
            <Input id="c-end" type="date" value={form.end_date ?? ''} onChange={(event) => setForm({ ...form, end_date: event.target.value })} />
          </div>
        </div>
        <div className="field">
          <label>{t('cycle.goal')}</label>
          <MarkdownEditor
            value={form.description ?? ''} minHeight={90}
            onChange={(value) => setForm({ ...form, description: value })}
          />
        </div>

        <ProjectScopeField
          id="c-scope"
          scope={choice.scope} onScope={choice.setScope}
          picked={choice.picked} onPicked={choice.setPicked}
          projects={projects}
          stranded={stranded}
          labels={{
            legend: t('cycle.scope'),
            this: t('cycle.scopeThis'), some: t('cycle.scopeSome'), all: t('cycle.scopeAll'),
            thisHint: t('cycle.scopeThisHint'), someHint: t('cycle.scopeSomeHint'), allHint: t('cycle.scopeAllHint'),
            pickOne: t('cycle.scopePickOne'),
            strandTitle: t('cycle.scopeStrandTitle', { count: stranded.length }),
            strandMore: t('cycle.scopeStrandMore', { count: stranded.length - 12 }),
            strandHint: t('cycle.scopeStrandHint'),
          }}
        />
      </Sheet>
      {dialog}
    </>
  );
}

/**
 * Making a milestone, and changing one — including what it covers.
 *
 * A milestone was the odd one out: its scope was chosen on the inline form that
 * creates it and never again, because it is otherwise edited through a card and
 * a menu, and the warning a re-scope needs has nowhere to sit on a card. So it
 * has a sheet now, for the same three fields a cycle's has — the name, the date
 * it is aimed at, and who works on it.
 *
 * `update_module` over MCP has always been able to re-scope one and report what
 * it stranded; this is the same move where the caller is a person.
 */
function ModuleEditor({ projectId, moduleId, onClose }: { projectId: string; moduleId?: string; onClose: () => void }) {
  const t = useT();
  const module_ = useRow('module', moduleId);
  const { confirm, dialog } = useConfirm();
  const [name, setName] = useState(module_?.name ?? '');
  const [target, setTarget] = useState(module_?.target_date ?? '');
  const choice = useScopeChoice(moduleId ? module_ : undefined, projectId);
  const projects = useScopeTargets();
  const stranded = useStranded('module_id', moduleId, choice.settled);

  const save = async () => {
    if (!name.trim() || choice.incomplete) return;
    const fields = { name: name.trim(), target_date: target || null };
    if (moduleId) {
      if (stranded.length
        && !(await confirm(t('module.scopeStrandConfirm', { count: stranded.length }), t('action.save')))) return;
      update('module', moduleId, { ...fields, ...choice.settled });
    } else {
      create('module', { ...fields, ...choice.settled, status: 'planned', sort_order: orderKey(null, null) });
    }
    onClose();
  };

  return (
    <>
      <Sheet
        title={moduleId ? t('module.edit') : t('module.new')}
        onClose={onClose}
        footer={(
          <Button variant="primary" onClick={() => void save()} disabled={!name.trim() || choice.incomplete}>
            {t('action.save')}
          </Button>
        )}
      >
        <div className="field">
          <label htmlFor="m-name">{t('module.name')}</label>
          <Input id="m-name" autoFocus value={name} onChange={(event) => setName(event.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="m-target">{t('module.target')}</label>
          <Input id="m-target" type="date" value={target ?? ''} onChange={(event) => setTarget(event.target.value)} />
        </div>

        <ProjectScopeField
          id="m-scope"
          scope={choice.scope} onScope={choice.setScope}
          picked={choice.picked} onPicked={choice.setPicked}
          projects={projects}
          stranded={stranded}
          labels={{
            legend: t('module.scope'),
            this: t('module.scopeThis'), some: t('module.scopeSome'), all: t('module.scopeAll'),
            thisHint: t('module.scopeThisHint'), someHint: t('module.scopeSomeHint'), allHint: t('module.scopeAllHint'),
            pickOne: t('module.scopePickOne'),
            strandTitle: t('module.scopeStrandTitle', { count: stranded.length }),
            strandMore: t('module.scopeStrandMore', { count: stranded.length - 12 }),
            strandHint: t('module.scopeStrandHint'),
          }}
        />
      </Sheet>
      {dialog}
    </>
  );
}

/**
 * The projects a cycle or a milestone could be given to.
 *
 * The same list QuickAdd offers, and scoped the same way: this workspace's (the
 * local store holds the ones from before a switch too), not archived, and no
 * containers — a container holds projects rather than tasks, so it is not
 * somewhere a cycle's work can be.
 */
function useScopeTargets() {
  const { workspaceId } = useSession();
  return useQuery(
    () => list('project', (p) => p.workspace_id === workspaceId && !p.archived && !p.is_container)
      .sort((a, b) => a.name.localeCompare(b.name)),
    [workspaceId],
  );
}

/**
 * The work a chosen scope would leave behind.
 *
 * Only ever on an edit — a cycle or a milestone being made holds nothing yet.
 * `coversProject` is the same function the server's SQL mirrors, so what this
 * warns about and what `update_cycle` reports cannot disagree.
 */
function useStranded(field: 'cycle_id' | 'module_id', id: string | undefined, settled: ProjectScope) {
  const projects = (settled.projects ?? []).join(',');
  return useQuery(
    () => (id
      ? list('task', (task) => task[field] === id && !coversProject(settled, task.project_id))
      : []),
    [field, id, settled.project_id, projects],
  );
}

/**
 * The way back out of a cycle or a milestone.
 *
 * Neither has a list of its own — both are a tab inside a project — so the
 * trail names the project and the tab, which is exactly the screen somebody
 * came from. `?tab=` is already how a link points at one, so this is the same
 * address the tab strip writes.
 *
 * A cycle or milestone several projects run has no single project to go back
 * to; the comments on both screens say so about scoping a view, and it is the
 * same fact here. Those get the project list, which is the honest answer rather
 * than picking one of the projects and calling it the parent.
 */
function planningCrumbs(projectId: string | null | undefined, tab: 'cycles' | 'modules', label: string): Crumb[] {
  const project = projectId ? byId('project', projectId) : undefined;
  return [
    { to: '/projects', label, icon: <Icon name="board" size={13} /> },
    ...(project ? [{ to: `/projects/${project.id}?tab=${tab}`, label: project.name, icon: project.icon }] : []),
  ];
}

export function CyclePage() {
  const t = useT();
  const navigate = useNavigate();
  const openTask = useOpenTask();
  const { id = '' } = useParams();
  const cycle = useRow('cycle', id);
  const [view, setView] = useState<ViewConfig>({ ...DEFAULT_VIEW, groupBy: 'state' });
  const [adding, setAdding] = useState(false);
  const tasks = useQuery(() => list('task', (t) => t.cycle_id === id), [id]);
  const visible = useVisibleTasks(tasks, view);

  const burndown = useMemo(() => {
    const done = tasks.filter((task) => isDoneGroup(byId('state', task.state_id)?.group_key)).length;
    const points = tasks.reduce((sum, task) => sum + (task.estimate ?? 0), 0);
    const donePoints = tasks
      .filter((task) => isDoneGroup(byId('state', task.state_id)?.group_key))
      .reduce((sum, task) => sum + (task.estimate ?? 0), 0);
    return { done, total: tasks.length, points, donePoints };
  }, [tasks]);

  // Without a `Header` this screen lost the app's chrome altogether — no title,
  // no sync pill, and nothing to press. A cycle that has been deleted still
  // opens from a link in a chat message.
  if (!cycle) {
    return (
      <>
        <Header title={t('cycle.title')} />
        <div className="px-3 pt-4 sm:px-6 sm:pt-5">
          <Trail parts={[{ to: '/projects', label: t('nav.projects'), icon: <Icon name="board" size={13} /> }]} />
        </div>
        <Empty
          emoji="🕳️" title={t('cycle.notFound')}
          action={<Button onClick={() => navigate('/projects')}>{t('nav.backToList')}</Button>}
        />
      </>
    );
  }

  return (
    <>
      <Header title={cycle.name}>
        {/* `?? undefined` on all three below, and it is the whole shared-cycle
            answer on this screen: a cycle several projects run has no single
            project to scope a view to or to file a new task into. QuickAdd
            already offers a picker when it is not told one, which is exactly
            the right question to ask here. */}
        <ViewControls view={view} onChange={setView} projectId={cycle.project_id ?? undefined} />
        <Button variant="primary" size="sm" onClick={() => setAdding(true)}><Icon name="plus" size={14} /></Button>
      </Header>
      <div className="mx-auto max-w-[1180px] px-3 pb-20 pt-4 sm:px-6 sm:pb-16 sm:pt-5">
        <Trail parts={planningCrumbs(cycle.project_id, 'cycles', t('nav.projects'))} />
        <div className="rounded-[var(--radius)] border border-line bg-raised p-3.5 mb-3.5">
          <div className="flex items-center gap-2 mb-2">
            <div className="flex-1 min-w-0">
              <strong>{t('cycle.taskProgress', { done: burndown.done, total: burndown.total })}</strong>
              <div className="text-muted text-[12.5px]">
                {t('cycle.pointProgress', { done: burndown.donePoints, total: burndown.points })}
                {cycle.start_date && cycle.end_date && ` · ${shortDate(cycle.start_date)} – ${shortDate(cycle.end_date)}`}
              </div>
            </div>
          </div>
          <Progress value={burndown.done} total={burndown.total} />
          {cycle.description && <div className="mt-2 text-[12.5px]"><Markdown source={cycle.description} /></div>}
        </div>
        {/* The sprint's own papers: the retro notes, the demo recording, the
            capacity sheet somebody built it from. Above the tasks, because it
            is about the cycle and the list below is about the work. */}
        <Attachments on={{ kind: 'cycle', id: cycle.id }} className="mb-3.5" />
        <TaskViews tasks={visible} view={view} projectId={cycle.project_id ?? undefined} onOpen={openTask} implied={{ cycleId: cycle.id }} />
      </div>
      {adding && <QuickAdd projectId={cycle.project_id ?? undefined} cycleId={id} onClose={() => setAdding(false)} />}
    </>
  );
}

/* ---------------------------------------------------------------- modules */

function Modules({ projectId }: { projectId: string }) {
  const t = useT();
  const navigate = useNavigate();
  const members = useMembers();
  // This project's own, plus the ones it shares — the same rule, and the same
  // function, the Cycles tab beside it follows.
  const modules = useQuery(() => list('module', (m) => coversProject(m, projectId)), [projectId]);
  /* A sheet rather than the one-line form this used to be. A milestone's scope
     was choosable here and nowhere else, so it could never be changed; moving
     the whole thing into an editor is what gives the warning somewhere to sit,
     and gives the name and the target date one place instead of two. */
  const [editing, setEditing] = useState<string | 'new' | null>(null);

  return (
    <div className="mx-auto max-w-[1180px] px-3 pb-20 pt-4 sm:px-6 sm:pb-16 sm:pt-5">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-[15px]">{t('module.title')}</h2>
        <span className="text-muted text-[12.5px]">{t('module.subtitle')}</span>
        <span className="flex-1 min-w-0" />
        <Button size="sm" onClick={() => setEditing('new')}><Icon name="plus" size={14} /> {t('module.new')}</Button>
      </div>

      {!modules.length && <Empty emoji="🎯" title={t('module.emptyTitle')} hint={t('module.emptyHint')} guide="planning" />}

      <div className="grid gap-3 sm:grid-cols-2">
        {modules.map((module) => {
          const tasks = list('task', (task) => task.module_id === module.id);
          const done = tasks.filter((task) => isDoneGroup(byId('state', task.state_id)?.group_key)).length;
          const lead = members.find((member) => member.id === module.lead_id);
          return (
            <div className="rounded-[var(--radius)] border border-line bg-raised p-3.5" key={module.id}>
              <div className="flex items-center gap-2 mb-1.5">
                <strong className="flex-1 min-w-0 truncate">{module.name}</strong>
                {/* Said on the card, because deleting a shared module takes it
                    out of every project in it and the menu beside this offers
                    exactly that. Counted when it names some: "Shared" alone
                    leaves the reader to guess whether that is two or twelve. */}
                {!module.project_id && (
                  <span
                    className={chipVariants()}
                    title={module.projects?.length ? t('module.sharedSomeHint') : t('module.sharedHint')}
                  >
                    {module.projects?.length
                      ? t('module.sharedCount', { count: module.projects.length })
                      : t('module.shared')}
                  </span>
                )}
                {lead && <Avatar user={lead} size={20} />}
                <MenuButton
                  variant="ghost" size="iconSm"
                  label={t('common.moreActions')}
                  items={[
                    { id: 'edit', label: t('action.edit'), onSelect: () => setEditing(module.id) },
                    ...members.map((member) => ({
                      id: member.id, section: t('project.lead'), label: member.name,
                      onSelect: () => update('module', module.id, { lead_id: member.id }),
                    })),
                    { id: 'delete', section: t('module.danger'), label: t('module.delete'), danger: true, onSelect: () => remove('module', module.id) },
                  ]}
                >
                  <Icon name="dots" size={14} />
                </MenuButton>
              </div>
              <Progress value={done} total={tasks.length} />
              <div className="flex items-center gap-2 text-muted text-[12.5px] mt-1.5">
                <span>{done}/{tasks.length}</span>
                <span className="flex-1 min-w-0" />
                <Input
                  inputSize="sm" type="date" style={{ width: 150, height: 28 }}
                  value={module.target_date ?? ''}
                  onChange={(event) => update('module', module.id, { target_date: event.target.value || null })}
                />
              </div>
              <Button size="sm" block className="mt-2" onClick={() => navigate(`/modules/${module.id}`)}>{t('action.open')}</Button>
            </div>
          );
        })}
      </div>

      {editing && (
        <ModuleEditor
          projectId={projectId}
          moduleId={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

export function ModulePage() {
  const t = useT();
  const navigate = useNavigate();
  const openTask = useOpenTask();
  const { id = '' } = useParams();
  const module = useRow('module', id);
  const [view, setView] = useState<ViewConfig>(DEFAULT_VIEW);
  const [editing, setEditing] = useState(false);
  const tasks = useQuery(() => list('task', (t) => t.module_id === id), [id]);
  const visible = useVisibleTasks(tasks, view);
  const canWrite = useCanWrite();
  const kpis = useFeature('kpi');
  if (!module) {
    return (
      <>
        <Header title={t('module.title')} />
        <div className="px-3 pt-4 sm:px-6 sm:pt-5">
          <Trail parts={[{ to: '/projects', label: t('nav.projects'), icon: <Icon name="board" size={13} /> }]} />
        </div>
        <Empty
          emoji="🕳️" title={t('module.notFound')}
          action={<Button onClick={() => navigate('/projects')}>{t('nav.backToList')}</Button>}
        />
      </>
    );
  }
  return (
    <>
      <Header title={module.name}>
        {/* A module's description has always been rendered as markdown here and
            has never had an editor anywhere — it could only be set through the
            API, an import or an assistant. Same Edit/Done as a page, because it
            is the same job. */}
        {canWrite && (
          <Button size="sm" onClick={() => setEditing(!editing)}>
            {editing ? <><Icon name="check" size={14} /> {t('action.done')}</> : t('action.edit')}
          </Button>
        )}
        {/* `?? undefined` for the same reason the cycle page does it: a module
            several projects work on has no single project to scope a view to. */}
        <ViewControls view={view} onChange={setView} projectId={module.project_id ?? undefined} />
      </Header>
      <div className="mx-auto max-w-[1180px] px-3 pb-20 pt-4 sm:px-6 sm:pb-16 sm:pt-5">
        <Trail parts={planningCrumbs(module.project_id, 'modules', t('nav.projects'))} />
        {editing ? (
          <div className="mb-3.5">
            <MarkdownEditor
              value={module.description ?? ''} minHeight={130} autoFocus
              attachTo={{ kind: 'module', id: module.id }}
              onChange={(value) => update('module', module.id, { description: value })}
            />
          </div>
        ) : module.description ? (
          <div className="mb-3.5"><Markdown source={module.description} /></div>
        ) : null}
        {/* What the milestone is being judged against: the spec, the signed
            acceptance criteria, the screenshots from the review. */}
        <Attachments on={{ kind: 'module', id: module.id }} className="mb-3.5" />
        <TaskViews tasks={visible} view={view} projectId={module.project_id ?? undefined} onOpen={openTask} implied={{ moduleId: module.id }} />
        {/* A milestone is described by what gets built; this is what has to be
            *true* by the time it lands. Renders nothing when nothing has been
            promised against it, rather than an empty frame. */}
        {kpis && <Suspense fallback={null}><MilestoneKpis moduleId={module.id} /></Suspense>}
      </div>
    </>
  );
}

/* ------------------------------------------------------------ pages + cfg */

function ProjectPages({ projectId }: { projectId: string }) {
  const t = useT();
  const navigate = useNavigate();
  const me = useMe();
  const pages = useQuery(() => list('page', (p) => p.project_id === projectId && !p.archived), [projectId]);

  return (
    <div className="mx-auto max-w-[1180px] px-3 pb-20 pt-4 sm:px-6 sm:pb-16 sm:pt-5">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-[15px]">{t('project.pagesTitle')}</h2>
        <span className="flex-1 min-w-0" />
        <Button size="sm" onClick={() => navigate(`/pages/${createPage({ project_id: projectId, title: t('common.untitled') }, me)}`)}>
          <Icon name="plus" size={14} /> {t('project.newPage')}
        </Button>
      </div>
      {!pages.length && <Empty emoji="📄" title={t('project.noPages')} hint={t('project.noPagesHint')} guide="pages" />}
      {pages.map((page) => (
        <button key={page.id} className="task-row text-left" style={{ width: '100%' }} onClick={() => navigate(`/pages/${page.id}`)}>
          <span>{page.icon ?? '📄'}</span>
          <span className="flex-1 min-w-0 truncate">{page.title}</span>
        </button>
      ))}
    </div>
  );
}

function ProjectSettings({ projectId }: { projectId: string }) {
  const t = useT();
  const project = useRow('project', projectId);
  const navigate = useNavigate();
  const members = useMembers();
  const toast = useToast();
  const { workspaceId } = useSession();
  const { confirm, dialog } = useConfirm();
  const states = useQuery(
    () => list('state', (s) => s.project_id === projectId).sort(byOrder),
    [projectId],
  );
  const labels = useQuery(() => list('label', (l) => l.project_id === projectId), [projectId]);
  const [newLabel, setNewLabel] = useState('');
  const [importing, setImporting] = useState(false);
  const [copying, setCopying] = useState(false);
  const [foreign, setForeign] = useState<{ document: unknown; found: Inspection } | null>(null);
  /**
   * A Kolibri file, waiting for one question: a new project, or this one?
   *
   * Asked rather than assumed, because the two are genuinely different
   * operations — one cannot damage anything and the other writes into work
   * people are doing — and because until now the answer was always "a new
   * one" whether that was wanted or not.
   */
  const [landing, setLanding] = useState<{ name: string; document?: unknown; archive?: ArrayBuffer } | null>(null);
  const [into, setInto] = useState<'new' | 'this'>('new');

  /**
   * A project as a document: for moving it to another instance, and for reading
   * it. The download is built here rather than by navigating to the endpoint,
   * so it goes through the same authenticated fetch as everything else.
   */
  async function exportJson(): Promise<void> {
    try {
      const doc = await api.get<unknown>(`/api/workspaces/${workspaceId}/projects/${projectId}/export`);
      const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(project?.key ?? 'project').toLowerCase()}.kolibri.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (problem) {
      toast(problem instanceof Error ? problem.message : t('transfer.failed'));
    }
  }

  /**
   * Read a file first, import it second.
   *
   * A Kolibri document goes straight in — it is this app's own shape and there
   * is nothing to warn anybody about. Anything from another tool is *converted*,
   * and what a converter leaves behind is the part worth reading before a
   * project appears, not after.
   */
  async function importJson(file: File): Promise<void> {
    try {
      // An archive is not inspected first: its document is inside it, and
      // unpacking a .zip in the browser to look would be a second reader of a
      // format the server already reads. The question below is the same one.
      if (file.name.endsWith('.zip')) {
        setInto('new');
        setLanding({ name: file.name, archive: await file.arrayBuffer() });
        return;
      }
      const document_ = JSON.parse(await file.text());
      const found = await api.post<Inspection>(`/api/workspaces/${workspaceId}/import/json/inspect`, { document: document_ });
      if (found.from === 'kolibri') {
        setInto('new');
        setLanding({ name: String(found.name || file.name), document: document_ });
        return;
      }
      setForeign({ document: document_, found });
    } catch (problem) {
      toast(problem instanceof Error ? problem.message : t('transfer.failed'));
    }
  }

  type ImportReport = {
    project: { id: string };
    counts: Record<string, number>;
    unmatched: string[];
    updated?: number;
    missingFiles?: string[];
  };

  async function finishJsonImport(document_: unknown, target?: string): Promise<void> {
    setForeign(null);
    setLanding(null);
    try {
      const result = await api.post<ImportReport>(
        `/api/workspaces/${workspaceId}/import/json`,
        { document: document_, project_id: target },
      );
      await report(result);
    } catch (problem) {
      toast(problem instanceof Error ? problem.message : t('transfer.failed'));
    }
  }

  /** The same for a `.zip`, which the server unpacks and reads for itself. */
  async function finishArchiveImport(archive: ArrayBuffer, target?: string): Promise<void> {
    setLanding(null);
    try {
      const query = new URLSearchParams({ workspace: workspaceId, ...(target ? { project_id: target } : {}) });
      await report(await api.postArchive<ImportReport>(`/api/import/archive?${query}`, archive));
    } catch (problem) {
      toast(problem instanceof Error ? problem.message : t('transfer.failed'));
    }
  }

  /**
   * Say what landed — and, when it is not all of it, what did not.
   *
   * A count on its own is a lie by omission the moment anything was left
   * behind, which is why the missing files are named rather than counted.
   */
  async function report(result: ImportReport): Promise<void> {
    await pull();
    const rows = Object.values(result.counts).reduce((sum, count) => sum + count, 0);
    if (result.missingFiles?.length) toast(t('transfer.missingFiles', { names: result.missingFiles.join(', ') }));
    if (result.updated) toast(t('transfer.merged', { count: result.updated, added: rows }));
    else {
      toast(result.unmatched.length
        ? t('transfer.importedWithGaps', { count: rows, names: result.unmatched.join(', ') })
        : t('transfer.imported', { count: rows }));
    }
    navigate(`/projects/${result.project.id}`);
  }
  // A project cannot be its own parent, and the server refuses a longer loop —
  // this list only keeps the obvious case out of the menu.
  const siblings = useQuery(
    () => list('project', (other) => other.id !== projectId && !other.archived && other.parent_id !== projectId),
    [projectId],
  );
  // Why the container box may be refused, said before it is clicked.
  const taskCount = useQuery(() => list('task', (task) => task.project_id === projectId).length, [projectId]);
  // Which project already holds this key, if any — the same comparison the
  // server makes, so the two agree about what a collision is.
  const keyTaken = useQuery(() => {
    const key = (byId('project', projectId)?.key ?? '').trim().toUpperCase();
    if (!key) return '';
    return list('project', (other) => other.id !== projectId && !other.archived
      && (other.key ?? '').trim().toUpperCase() === key)[0]?.name ?? '';
  }, [projectId, project?.key]);
  if (!project) return null;

  return (
    <div className="mx-auto max-w-[1180px] px-3 pb-20 pt-4 sm:px-6 sm:pb-16 sm:pt-5" style={{ maxWidth: 620 }}>
      {/* Icon beside name, the shape the create form uses — the screen that
          makes a project and the screen that changes one should not lay the
          same two fields out differently. */}
      <div className="flex items-start gap-2.5">
        <div className="field" style={{ width: 60 }}>
          <label htmlFor="s-icon">{t('project.icon')}</label>
          <EmojiPicker
            id="s-icon" value={project.icon} fallback="🚀"
            onChange={(next) => update('project', projectId, { icon: next })}
          />
        </div>
        <div className="field flex-1 min-w-0">
          <label htmlFor="s-name">{t('project.name')}</label>
          <Input id="s-name" value={project.name} onChange={(event) => update('project', projectId, { name: event.target.value })} />
        </div>
      </div>
      <div className="field">
        <label>{t('project.description')}</label>
        <MarkdownEditor
          value={project.description ?? ''} minHeight={110}
          attachTo={{ kind: 'project', id: projectId }}
          onChange={(value) => update('project', projectId, { description: value })}
        />
      </div>

      {/* The project's own papers — the statement of work, the brief, the
          signed quote. Under the description because that is the other place
          the project describes itself, and in Settings rather than on the task
          list because a contract is read twice a year and the board is read
          twice an hour. */}
      <Attachments on={{ kind: 'project', id: projectId }} className="mb-4" />

      {/*
        Both of these could only ever be chosen once, at the moment the project
        was created, and never again — so a project made private by accident
        stayed private, and one whose name changed kept a key that no longer
        matched it.

        The key is the sharper of the two, because changing it does not go back
        and rewrite the identifiers already minted from it: BUS-39 stays BUS-39
        while the next task becomes NEW-40. That is the honest behaviour — a
        pasted identifier has to keep pointing at what it pointed at — but it is
        only honest if it is said, so it is said under the field.
      */}
      <div className="field-row">
        <div className="field">
          <label htmlFor="s-key">{t('project.key')}</label>
          <Input className="mono" id="s-key" value={project.key ?? ''} maxLength={6}
            onChange={(event) => update('project', projectId, { key: event.target.value.toUpperCase() })} />
          {/* The server bounces a taken key back, which on its own is a field
              that refuses to be typed in for no stated reason. This is the
              reason, said while it is being typed. */}
          {keyTaken
            ? <span className="text-[12px]" style={{ color: 'var(--danger)' }}>{t('project.keyTaken', { name: keyTaken })}</span>
            : <span className="text-[12px] text-muted">{t('project.keyChangeHint')}</span>}
        </div>
        <div className="field">
          <label htmlFor="s-vis">{t('project.visibility')}</label>
          <Select id="s-vis" value={project.visibility ?? 'public'}
            onChange={(event) => update('project', projectId, { visibility: event.target.value })}>
            <option value="public">{t('project.visibilityPublic')}</option>
            <option value="private">{t('project.visibilityPrivate')}</option>
          </Select>
        </div>
      </div>
      {/*
        A container is refused by the server while the project still has tasks,
        and `forced` sends the old value back — so the box un-ticks itself. The
        sentence under it is why, said before somebody clicks rather than after.
      */}
      <label className="check-row">
        <input
          type="checkbox"
          checked={!!project.is_container}
          onChange={(event) => update('project', projectId, { is_container: event.target.checked ? 1 : 0 })}
        />
        <span>
          {t('project.isContainer')}
          <span className="block text-[12px] text-muted">
            {taskCount > 0 ? t('project.isContainerBusy', { count: taskCount }) : t('project.isContainerHint')}
          </span>
        </span>
      </label>

      <div className="field-row">
        <div className="field flex-1 min-w-0">
          <label htmlFor="s-parent">{t('project.parent')}</label>
          <Select
            id="s-parent" value={project.parent_id ?? ''}
            onChange={(event) => update('project', projectId, { parent_id: event.target.value || null })}
          >
            <option value="">{t('project.parentNone')}</option>
            {siblings.map((other) => <option key={other.id} value={other.id}>{other.icon} {other.name}</option>)}
          </Select>
          <span className="text-[12px] text-muted">{t('project.parentHint')}</span>
        </div>
        <div className="field flex-1 min-w-0">
          <label htmlFor="s-start">{t('project.startDate')}</label>
          <Input id="s-start" type="date" value={project.start_date ?? ''}
            onChange={(event) => update('project', projectId, { start_date: event.target.value || null })} />
        </div>
      </div>

      <div className="field-row">
        <div className="field flex-1 min-w-0">
          <label htmlFor="s-lead">{t('project.lead')}</label>
          <Select id="s-lead" value={project.lead_id ?? ''} onChange={(event) => update('project', projectId, { lead_id: event.target.value || null })}>
            <option value="">{t('common.nobody')}</option>
            {members.map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}
          </Select>
        </div>
        <div className="field flex-1 min-w-0">
          <label htmlFor="s-target">{t('project.targetDate')}</label>
          <Input id="s-target" type="date" value={project.target_date ?? ''}
            onChange={(event) => update('project', projectId, { target_date: event.target.value || null })} />
        </div>
      </div>

      {/* Which days the *scheduler* counts. Not a lock: a bar dragged onto a
          Saturday stays there, because somebody who did that meant it. */}
      <div className="field">
        <label>{t('project.workingDays')}</label>
        <div className="flex items-center flex-wrap gap-1" role="group" aria-label={t('project.workingDays')}>
          {WEEKDAYS.map(({ day, key }) => {
            const days = project.working_days ?? DEFAULT_WORKING_DAYS;
            const on = days.includes(day);
            return (
              <button
                key={day}
                type="button"
                className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), on && 'bg-active text-fg')}
                style={on ? { background: 'var(--bg-active)' } : undefined}
                aria-pressed={on}
                onClick={() => update('project', projectId, {
                  working_days: on ? days.filter((d) => d !== day) : [...days, day].sort(),
                })}
              >
                {t(key)}
              </button>
            );
          })}
        </div>
        <span className="text-[12px] text-muted">{t('project.workingDaysHint')}</span>
      </div>

      <div className="flex items-center gap-2" style={{ margin: '18px 0 8px' }}>
        <SectionHeading tight>{t('project.workflowStates')}</SectionHeading>
        <span className="flex-1 min-w-0" />
        <GuideHint to="hierarchy" />
      </div>
      {states.map((state, at) => (
        <div className="stack-card" key={state.id}>
          {/* The name and the two ways to be rid of the state: everything that
              fits on one line at any width, on one line. */}
          <div className="flex items-center gap-2">
            <input
              type="color" value={state.color} style={{ width: 28, height: 28, border: 'none', background: 'none' }}
              aria-label={t('project.stateColour')}
              onChange={(event) => update('state', state.id, { color: event.target.value })}
            />
            <Input className="flex-1 min-w-0" value={state.name} aria-label={t('project.name')}
              onChange={(event) => update('state', state.id, { name: event.target.value })} />
            {/* The order here is the order of the board's columns — the same
                fractional keys the cards use, applied one level up. */}
            <Button variant="ghost" size="icon" aria-label={t('state.moveUp')} disabled={at === 0}
              onClick={() => update('state', state.id, {
                sort_order: orderKey(states[at - 2]?.sort_order ?? null, states[at - 1]?.sort_order ?? null),
              })}>
              <Icon name="chevronUp" size={14} />
            </Button>
            <Button variant="ghost" size="icon" aria-label={t('state.moveDown')} disabled={at === states.length - 1}
              onClick={() => update('state', state.id, {
                sort_order: orderKey(states[at + 1]?.sort_order ?? null, states[at + 2]?.sort_order ?? null),
              })}>
              <Icon name="chevronDown" size={14} />
            </Button>
            <Button variant="ghost" size="icon" aria-label={t('project.deleteState')} onClick={async () => {
              if (states.length <= 1) return;
              if (await confirm(t('project.deleteStateConfirm', { name: state.name }))) remove('state', state.id);
            }}>
              <Icon name="trash" size={14} />
            </Button>
          </div>
          {/*
            The three settings were a 140px select and two labels sitting to the
            left of their own controls — so on a phone "Limit" put its box at one
            indent, "Who may move work here" wrapped onto two lines and put its
            box at another, and both selects cut their longest option in half.
            The same rows the form above uses fix all of it: label over control,
            one column when there is no room for two.
          */}
          <div className="field-row mt-2">
            <div className="field">
              <label htmlFor={`st-group-${state.id}`}>{t('view.groupState')}</label>
              <Select id={`st-group-${state.id}`} value={state.group_key}
                onChange={(event) => update('state', state.id, { group_key: event.target.value })}>
                {STATE_GROUPS.map((group) => (
                  <option key={group} value={group}>{t(groupKey(group))}</option>
                ))}
              </Select>
            </div>
            <div className="field">
              <label htmlFor={`st-wip-${state.id}`}>{t('state.wipLimit')}</label>
              {/* Two digits never need a full column; the label above it still
                  starts on the same edge as its neighbours, which is the part
                  that has to line up. */}
              <Input id={`st-wip-${state.id}`} type="number" min={0} max={99} style={{ maxWidth: 110 }}
                value={state.wip_limit || ''} placeholder="0"
                onChange={(event) => update('state', state.id, { wip_limit: Number(event.target.value) || 0 })}
              />
            </div>
            <div className="field">
              <label htmlFor={`st-roles-${state.id}`}>{t('state.allowedRoles')}</label>
              <Select id={`st-roles-${state.id}`}
                value={state.allowed_roles?.[0] ?? ''}
                onChange={(event) => update('state', state.id, { allowed_roles: event.target.value ? [event.target.value] : [] })}
              >
                <option value="">{t('state.allowedAnyone')}</option>
                {(['member', 'admin', 'owner'] as const).map((role) => (
                  <option key={role} value={role}>{t(roleKey(role))}</option>
                ))}
              </Select>
            </div>
          </div>
        </div>
      ))}
      <p className="text-[12px] text-muted mt-1">{t('state.wipLimitHint')}</p>
      <p className="text-[12px] text-muted mb-2">{t('state.allowedHint')}</p>
      <Button size="sm" className="mt-1.5"
        onClick={() => create('state', {
          project_id: projectId, name: t('project.newStateName'), group_key: 'unstarted', color: '#64748b',
          sort_order: orderKey(states[states.length - 1]?.sort_order ?? null, null),
        })}
      >
        <Icon name="plus" size={14} /> {t('project.addState')}
      </Button>

      <SectionHeading>{t('field.settingsTitle')}</SectionHeading>
      <ProjectFields projectId={projectId} />

      <SectionHeading>{t('project.labels')}</SectionHeading>
      <div className="flex items-center flex-wrap gap-1.5 mb-2">
        {labels.map((label) => (
          <span className={chipVariants({ interactive: true })} key={label.id} onClick={() => remove('label', label.id)} title={t('project.labelRemoveHint')}>
            <span className={chipDot} style={{ background: label.color }} /> {label.name} ✕
          </span>
        ))}
      </div>
      <form
        className="flex items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (!newLabel.trim()) return;
          create('label', { project_id: projectId, name: newLabel.trim(), color: '#6366f1' });
          setNewLabel('');
        }}
      >
        <Input placeholder={t('project.newLabel')} value={newLabel} onChange={(event) => setNewLabel(event.target.value)} />
        <Button type="submit"><Icon name="plus" size={14} /></Button>
      </form>

      <SectionHeading>{t('time.title')}</SectionHeading>
      <ProjectTime projectId={projectId} />

      <SectionHeading>{t('import.title')}</SectionHeading>
      <div className="flex items-center gap-2 flex-wrap">
        <Button onClick={() => setImporting(true)}>
          <Icon name="attach" size={14} /> {t('import.action')}
        </Button>
        <Button onClick={() => void exportJson()}>
          <Icon name="page" size={14} /> {t('transfer.export')}
        </Button>
        {/* The archive and the spreadsheet are ordinary downloads rather than
            a fetch into a blob: a project with its screenshots in it is large,
            and a same-origin navigation carries the session cookie anyway. */}
        <Button onClick={() => { window.location.href = `/api/workspaces/${workspaceId}/projects/${projectId}/export.zip`; }}>
          <Icon name="archive" size={14} /> {t('transfer.exportZip')}
        </Button>
        <Button onClick={() => { window.location.href = `/api/workspaces/${workspaceId}/export/tasks.csv?project_id=${projectId}`; }}>
          <Icon name="table" size={14} /> {t('transfer.exportCsv')}
        </Button>
        <label className={cn(buttonVariants({  }), 'cursor-pointer')}>
          <Icon name="plus" size={14} /> {t('transfer.import')}
          <input
            type="file" accept=".json,.zip,application/json,application/zip" style={{ display: 'none' }}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) void importJson(file);
            }}
          />
        </label>
      </div>
      <p className="text-[12px] text-muted mt-1.5">{t('transfer.hint')}</p>
      {importing && <ImportSheet projectId={projectId} onClose={() => setImporting(false)} />}
      {landing && (
        <Sheet
          title={t('transfer.import')}
          onClose={() => setLanding(null)}
          footer={
            <>
              <Button onClick={() => setLanding(null)}>{t('action.cancel')}</Button>
              <Button
                variant="primary"
                onClick={() => {
                  const target = into === 'this' ? projectId : undefined;
                  if (landing.archive) void finishArchiveImport(landing.archive, target);
                  else void finishJsonImport(landing.document, target);
                }}
              >
                {t('data.importConfirm')}
              </Button>
            </>
          }
        >
          <p>{t('data.importAbout', { name: landing.name })}</p>
          <div className="field">
            <label htmlFor="import-into">{t('transfer.into')}</label>
            <Select id="import-into" value={into} onChange={(event) => setInto(event.target.value as 'new' | 'this')}>
              <option value="new">{t('transfer.intoNew')}</option>
              <option value="this">{t('transfer.intoThis')}</option>
            </Select>
          </div>
          <p className="text-[13px] text-muted">
            {into === 'new' ? t('transfer.intoNewHint') : t('transfer.intoThisHint')}
          </p>
        </Sheet>
      )}
      {foreign && (
        <ForeignImportSheet
          found={foreign.found}
          onClose={() => setForeign(null)}
          onImport={() => void finishJsonImport(foreign.document)}
        />
      )}

      <SectionHeading>{t('copy.title')}</SectionHeading>
      <Button onClick={() => setCopying(true)}>
        <Icon name="copy" size={14} /> {t('copy.action')}
      </Button>
      {copying && (
        <CopyProjectSheet
          projectId={projectId}
          onClose={() => setCopying(false)}
          onCopied={(id) => navigate(`/projects/${id}`)}
        />
      )}

      <div className="my-2 h-px bg-line" style={{ margin: '22px 0' }} />
      <div className="flex items-center gap-2">
        <Button onClick={() => update('project', projectId, { archived: project.archived ? 0 : 1 })}>
          <Icon name="archive" size={14} /> {project.archived ? t('project.unarchive') : t('project.archive')}
        </Button>
        <Button variant="danger"
          onClick={async () => {
            if (await confirm(t('project.deleteConfirm', { name: project.name }))) {
              remove('project', projectId);
              navigate('/');
            }
          }}
        >
          <Icon name="trash" size={14} /> {t('project.delete')}
        </Button>
      </div>
      {dialog}
    </div>
  );
}
